import { runInAction, when } from "mobx";
import type { FetchByQueryResult, Segment } from "mobx-repository";
import { prop } from "ramda";
import type {
    PageInfo,
    VehicleGroupCreate,
    VehicleGroupsApi,
    VehicleGroupsListVehicleGroupsRequest,
    VehicleGroupSortKey,
    VehicleGroupUpdate,
    VehicleGroup,
} from "../../api";
import type { ApiResource } from "../../utils/elofleet-repository";
import { ElofleetRepository } from "../../utils/elofleet-repository";
import type { UUID } from "../../utils/uuid";
import { Validation } from "../../utils/validation";
import type { ServiceOwnUser } from "../services/service-own-user";
import { defaultPageSize } from "../../utils/constants";

export type VehicleGroupsQuery = Omit<VehicleGroupsListVehicleGroupsRequest, "page">;

export interface VehicleGroupsApiResource extends ApiResource {
    entity: VehicleGroup;
    query: VehicleGroupsQuery;
    update: VehicleGroupUpdate;
    create: VehicleGroupCreate;
    sortKey: VehicleGroupSortKey;
}

export class RepositoryVehicleGroups extends ElofleetRepository<VehicleGroupsApiResource> {
    constructor(
        private readonly vehicleGroupsApi: VehicleGroupsApi,
        private readonly serviceOwnUser: ServiceOwnUser,
    ) {
        super();
        // Preload the first page once the user is logged in to avoid loading them individually later.
        when(
            () => this.serviceOwnUser.user !== undefined,
            () =>
                this.byQuery(
                    {
                        pageSize: defaultPageSize,
                    },
                    { count: this.defaultCount },
                ),
        );
    }

    public validation = {
        create: new Validation((vehicleGroupCreate: VehicleGroupCreate) =>
            this.vehicleGroupsApi.vehicleGroupsValidateCreateVehicleGroup({
                vehicleGroupCreate: vehicleGroupCreate,
            }),
        ),
        update: new Validation((vehicleGroupUpdate: VehicleGroupUpdate, vehicleGroupId: UUID) =>
            this.vehicleGroupsApi.vehicleGroupsValidateUpdateVehicleGroup({
                vehicleGroupId,
                vehicleGroupUpdate,
            }),
        ),
    };

    protected async fetchByQuery(
        query: VehicleGroupsQuery,
        pagination: Segment,
    ): Promise<FetchByQueryResult<VehicleGroup>> {
        const page = pagination.offset / this.defaultCount;
        const entities = await this.vehicleGroupsApi.vehicleGroupsListVehicleGroups({
            ...query,
            page,
        });
        return { entities };
    }

    public fetchMetadata(_query: unknown): Promise<PageInfo> {
        return this.vehicleGroupsApi.vehicleGroupsListVehicleGroupsMetadata({
            pageSize: defaultPageSize,
        });
    }

    protected async fetchById(vehicleGroupId: UUID): Promise<VehicleGroup | undefined> {
        return await this.vehicleGroupsApi.vehicleGroupsReadVehicleGroup({
            vehicleGroupId,
        });
    }

    protected extractId = prop("id");

    public async update(
        vehicleGroupId: UUID,
        vehicleGroupUpdate: VehicleGroupUpdate,
    ): Promise<VehicleGroup> {
        const vehicleGroup = await this.vehicleGroupsApi.vehicleGroupsUpdateVehicleGroup({
            vehicleGroupId,
            vehicleGroupUpdate,
        });
        await this.waitForIdle();
        runInAction(() => {
            this.reloadQuery({
                pageSize: defaultPageSize,
            });

            this.add(vehicleGroup);
        });
        return vehicleGroup;
    }

    public async create(vehicleGroupCreate: VehicleGroupCreate): Promise<VehicleGroup> {
        const vehicleGroup = await this.vehicleGroupsApi.vehicleGroupsCreateVehicleGroup({
            vehicleGroupCreate,
        });
        await this.waitForIdle();
        runInAction(() => {
            this.reloadQuery({
                pageSize: defaultPageSize,
            });
            this.add(vehicleGroup);
        });
        return vehicleGroup;
    }

    public async delete(...vehicleGroupIds: UUID[]): Promise<void> {
        await Promise.all(
            vehicleGroupIds.map((vehicleGroupId) =>
                this.vehicleGroupsApi.vehicleGroupsDeleteVehicleGroup({ vehicleGroupId }),
            ),
        );
        await this.waitForIdle();
        this.reloadQuery({
            pageSize: defaultPageSize,
        });
    }
}
